import { useEffect, useRef } from 'react'
import { RotateCcw } from 'lucide-react'
import { SetupAssistantMessage } from './SetupAssistantMessage'

function relativeTime(ts) {
  if (!ts) return null
  const diff = Math.floor((Date.now() - ts) / 1000)
  if (diff < 60) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function SetupMessageTimeline({ messages, onRetry, children }) {
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
        {messages.map((message) => {
          const isUser = message.role === 'user'
          const hasFailed = isUser && message.error
          return (
            <div
              key={message.id}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              <div className={`max-w-[80%] ${isUser ? '' : 'max-w-[88%]'}`}>
                <div
                  className={`rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${
                    hasFailed
                      ? 'border border-red-200 bg-red-50 text-red-800'
                      : isUser
                        ? 'bg-accent-700 text-white'
                        : 'text-gray-800'
                  }`}
                >
                  {isUser ? (
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  ) : message.isTyping ? (
                    <div className="flex items-center gap-1.5 py-1 px-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  ) : (
                    <SetupAssistantMessage content={message.content} />
                  )}
                </div>
                {(message.timestamp || hasFailed) && (
                  <div className={`mt-1 flex items-center gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
                    {message.timestamp && !message.isTyping && (
                      <span className="text-[10px] text-gray-300">{relativeTime(message.timestamp)}</span>
                    )}
                    {hasFailed && (
                      <>
                        <span className="text-[10px] text-red-400">{message.error}</span>
                        {onRetry && (
                          <button
                            type="button"
                            onClick={() => onRetry(message)}
                            className="inline-flex items-center gap-1 text-[10px] font-medium text-red-500 hover:text-red-700"
                          >
                            <RotateCcw className="w-3 h-3" />
                            Retry
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {children}
        <div ref={endRef} />
      </div>
    </div>
  )
}

export default SetupMessageTimeline
